import { useEffect } from "react";
import { useParams } from "react-router";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAsync } from "../lib/useAsync";
import { IconCall, IconStar, IconVerified, IconWhatsApp } from "../ui/icons";
import { Empty, ErrorState, ListingCard, Loading, PageHeader, TrustBadges, TrustLine, type ListingSummary, type Trust } from "../ui/kit";

// Every member's page: who they are, their track record on Walashy, what they have on sale, and what the
// people they dealt with said (as a seller and as a buyer).

type Seller = {
  id: string;
  displayName: string;
  city: string | null;
  verified: boolean;
  memberSince: string;
  rating: { average: number | null; count: number };
  trust: Trust;
  contact: { whatsapp: boolean; calls: boolean };
  reviews: { rating: number; body: string; createdAt: string; item: string; as: "seller" | "buyer"; by: string | null }[];
  business: { name: string; crNumber: string; vatNumber: string | null } | null;
};

export function SellerPage() {
  const { id } = useParams();
  const { t, date } = useI18n();
  const seller = useAsync(() => api<Seller>(`/api/sellers/${id}`), [id]);
  const listings = useAsync(() => api<{ results: ListingSummary[]; total: number }>(`/api/catalog/search?sellerId=${id}&sort=newest&limit=96`), [id]);
  useEffect(() => {
    if (seller.data) document.title = `${seller.data.displayName} · Walashy`;
  }, [seller.data]);

  if (seller.loading && !seller.data) return <Loading />;
  if (seller.error || !seller.data) return <ErrorState error={seller.error} onRetry={seller.reload} />;
  const s = seller.data;
  const total = listings.data?.total ?? 0;

  return (
    <div className="seller-page">
      <PageHeader title={s.displayName} back />
      <section className="seller-head">
        <span className="avatar lg" aria-hidden>{s.displayName.slice(0, 1)}</span>
        <div className="stack" style={{ gap: 4 }}>
          <div className="row wrap">
            {s.verified && <span className="tag good"><IconVerified size={14} /> {t("seller.verified")}</span>}
            {s.city && <span className="small muted" dir="auto">{s.city}</span>}
          </div>
          <span className="small muted">
            {s.rating.count ? (
              <><IconStar size={14} /> {t("listing.ratingLine", { avg: s.rating.average ?? "", n: s.rating.count })}</>
            ) : t("listing.noReviews")}
            {" · "}{t("listing.memberSince", { d: date(s.memberSince, { month: "short", year: "numeric" }) })}
          </span>
          {s.business && (
            <span className="small muted">
              {t("shop.registered")}: <span dir="auto">{s.business.name}</span> · {t("shop.cr")} <span dir="ltr">{s.business.crNumber}</span>
              {s.business.vatNumber && <> · {t("shop.vat")} <span dir="ltr">{s.business.vatNumber}</span></>}
            </span>
          )}
          <span className="small"><TrustLine trust={s.trust} />{s.trust.purchases ? ` · ${t("trust.purchases", { n: s.trust.purchases })}` : ""}</span>
          <TrustBadges trust={s.trust} />
          {(s.contact.whatsapp || s.contact.calls) && (
            <span className="tiny muted row" style={{ gap: 6 }}>
              {s.contact.whatsapp && <><IconWhatsApp size={14} /> {t("contact.whatsappOk")}</>}
              {s.contact.calls && <><IconCall size={14} /> {t("seller.takesCalls")}</>}
            </span>
          )}
        </div>
      </section>

      <section aria-labelledby="seller-listings" className="seller-section">
        <h2 id="seller-listings">{t("seller.listings", { n: total })}</h2>
        {listings.loading && !listings.data ? (
          <Loading />
        ) : listings.error ? (
          <ErrorState error={listings.error} onRetry={listings.reload} />
        ) : total === 0 ? (
          <Empty title={t("seller.noListings")} body={t("seller.noListingsBody")} />
        ) : (
          <div className="grid">
            {listings.data!.results.map((l, i) => <ListingCard key={l.id} l={l} index={i} />)}
          </div>
        )}
      </section>

      <section aria-labelledby="seller-reviews" className="seller-section">
        <h2 id="seller-reviews">{t("seller.reviews")}</h2>
        {s.reviews.length === 0 ? (
          <p className="muted">{t("seller.noReviews")}</p>
        ) : (
          <ul className="seller-reviews">
            {s.reviews.map((r, i) => (
              <li key={i}>
                <span className="seller-stars" aria-label={t("seller.stars", { n: r.rating })}>{"★".repeat(r.rating)}<span className="muted">{"★".repeat(5 - r.rating)}</span></span>
                {r.body && <p dir="auto">{r.body}</p>}
                <span className="tiny muted" dir="auto">
                  {t(r.as === "seller" ? "seller.asSeller" : "seller.asBuyer")}{r.by ? ` · ${r.by}` : ""} · {r.item} · {date(r.createdAt, { month: "short", year: "numeric" })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
